import { serverGet } from "../../middleware/http";
import { getSessionStorage, setSessionStorage } from "./validation";
import { clearHandoff } from "./ssoHandoff";

const CACHE_VERSION = "v1";
const STORE_KEY = "cpRequestCache";
const PERSIST_MAX_CHARS = 32 * 1024;

// Deliberately an allowlist, never a general-purpose GET cache. Every entry here is either
// account/tenant configuration or reference data — nothing that varies by patient, and nothing a
// patient can change mid-session. Contact and profile reads are absent on purpose: PUP can rewrite
// them while the flow is running, and SPA would then prefill a stale address.
const CONFIG_PREFIXES = [
    "setting/getaccountsetting",
    "setting/getaccountcountry",
    "setting/getsettingoptions",
    "setting/getaccountmodules",
    "setting/accountpartners",
    "setting/specialityreason",
    "dcspeciality/getprotocolinfo",
    "calender/getcateogry",
    "category/get",
    "country/getlist",
    "dcservice/service",
    "dcspeciality/specialityservice",
    "dcprovider/getlicenseregion",
    "dcprovider/getspeciality",
    "dcpharmacy/recommenedpharmacy",
    "dcdiscount/list",
    "dcpayment/getpaymentsettings",
    "patienthealthmetric/gethealthmetrics",
];

const values = { config: new Map(), flow: new Map() };

// Keyed by url and shared across stores, so a prefetch and the consumer that follows it can never
// both be in flight for the same request.
const inFlight = new Map();

let hydrated = false;

// Prefetched step data, served exactly ONCE. A step's own loader is free to refetch on later
// entries -- Service.js's ITM deliberately refetches the forms list every pass so a formInputId
// created mid-flow is picked up after Back -- so a durable cache here would be a bug. Take-once
// serves only the entry the prefetch was started for; every later entry fetches fresh.
const warmed = new Map();

const isConfigCacheable = (url) => CONFIG_PREFIXES.some((prefix) => url.startsWith(prefix));

// Speculative requests run through a bounded queue, never straight at the network. Two reasons:
// the CRA dev server is HTTP/1.1, where six connections per origin are shared with the requests the
// patient is actually waiting on, and every call costs the same ~950ms fixed floor whether or not
// anyone reads the answer. Same worker-pool shape as docChunkUpload.js.
const PREFETCH_CONCURRENCY = 3;
const queue = [];
let active = 0;

const onIdle = (cb) =>
    typeof window !== "undefined" && typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(cb)
        : setTimeout(cb, 0);

const pump = () => {
    while (active < PREFETCH_CONCURRENCY && queue.length) {
        const task = queue.shift();
        active += 1;
        Promise.resolve()
            .then(task)
            .catch((e) => console.error(e))
            .then(() => {
                active -= 1;
                pump();
            });
    }
};

const enqueue = (task) => {
    queue.push(task);
    onIdle(pump);
};

// A warm entry is a deferred, not an in-flight request: it may sit in the queue for a while. If the
// consumer asks for it before the queue gets to it, run() promotes it and it starts immediately --
// so a queued prefetch can never make the consumer wait longer than fetching would have.
const makeWarmEntry = (url, revalidate) => {
    let started = false;
    let settle;
    const promise = new Promise((resolve) => {
        settle = resolve;
    });

    const entry = { promise, revalidate, settled: false, value: undefined };

    entry.run = () => {
        if (!started) {
            started = true;
            serverGet(url)
                .catch((e) => {
                    console.error(e);
                    return undefined;
                })
                .then((res) => {
                    entry.settled = true;
                    entry.value = res;
                    settle(res);
                });
        }
        return promise;
    };

    return entry;
};

const hydrate = () => {
    if (hydrated) return;
    hydrated = true;

    const stored = getSessionStorage(STORE_KEY);

    if (stored?.version !== CACHE_VERSION || !stored?.entries) return;

    Object.entries(stored.entries).forEach(([url, value]) => values.config.set(url, value));
};

const persist = () => {
    const entries = {};

    values.config.forEach((value, url) => {
        const serialised = JSON.stringify(value);
        if (serialised && serialised.length <= PERSIST_MAX_CHARS) entries[url] = value;
    });

    setSessionStorage(STORE_KEY, { version: CACHE_VERSION, entries });
};

// serverGet resolves with undefined when handleAuthError swallowed a 401, and with error?.response
// otherwise — neither is a usable envelope, and caching either would pin the failure in place.
const isUsable = (res) => !!res && res.status == 1;

/**
 * Reads through the cache for the given URL. Concurrent callers share one request, so warming a URL
 * early and asking for it later costs exactly one round trip. The URL is the key, which is what
 * makes this safe: every value that changes the answer (fmid, spid, rsid, eid, paytypecode …) is
 * already part of it, so a changed parameter is a miss rather than a stale hit.
 */
export const cachedGet = async (url, { store = "config" } = {}) => {
    if (store === "config" && !isConfigCacheable(url)) return await serverGet(url);

    if (store === "config") hydrate();

    const cached = values[store].get(url);
    if (cached !== undefined) return cached;

    if (inFlight.has(url)) return await inFlight.get(url);

    const request = (async () => {
        const res = await serverGet(url);

        if (isUsable(res)) {
            values[store].set(url, res);
            if (store === "config") persist();
        }

        return res;
    })();

    inFlight.set(url, request);

    try {
        return await request;
    } catch (e) {
        console.error(e);
        return undefined;
    } finally {
        inFlight.delete(url);
    }
};

/**
 * Starts requests for a step the patient has not reached yet. Never awaited and never allowed to
 * surface an error — the consumer calls cachedGet for the same URL and joins whatever is in flight.
 */
export const prime = (urls, options) => {
    (Array.isArray(urls) ? urls : [urls]).filter(Boolean).forEach((url) => {
        enqueue(() => cachedGet(url, options).catch((e) => console.error(e)));
    });
};

/**
 * Starts a step's requests before the patient reaches it. Never awaited; a failure resolves to
 * undefined so the consumer falls through to its own fetch.
 */
/**
 * @param refresh Replace an entry that is already warmed instead of skipping it. Needed wherever
 * the same URL can have a NEW answer -- REC submit is the case that matters: the reason may have
 * been edited, which changes which intake forms apply, and without this the dedupe guard would
 * keep serving the list warmed under the previous reason.
 */
export const warm = (urls, { revalidate = false, refresh = false } = {}) => {
    (Array.isArray(urls) ? urls : [urls]).filter(Boolean).forEach((url) => {
        if (warmed.has(url)) {
            if (!refresh) return;
            warmed.delete(url);
        }
        const entry = makeWarmEntry(url, revalidate);
        warmed.set(url, entry);
        enqueue(entry.run);
    });
};

/**
 * Serves a warmed entry, otherwise fetches.
 *
 * Two modes, chosen when the URL was warmed, not here:
 *  - take-once (default) -- the entry is consumed and dropped, so a later visit to the same step
 *    refetches. Required for anything the flow itself rewrites: ITM's forms list carries a
 *    formInputId created mid-flow, HPF submits the vitals ITM reads back, SPA writes the shipment
 *    it later reads. Serving those from cache is a stale-data bug, not an optimisation.
 *  - revalidate -- returns the cached payload immediately and refreshes behind it, calling onFresh
 *    with the new value if the caller wants to re-render. Only for payloads nothing between two
 *    visits can change.
 */
export const warmGet = async (url, onFresh) => {
    if (warmed.has(url)) {
        const entry = warmed.get(url);

        if (entry.revalidate && entry.settled) {
            const cached = entry.value;

            enqueue(() =>
                serverGet(url)
                    .then((res) => {
                        if (isUsable(res)) entry.value = res;
                        if (typeof onFresh === "function") onFresh(res);
                    })
                    .catch((e) => console.error(e))
            );

            return cached;
        }

        if (!entry.revalidate) warmed.delete(url);
        return await entry.run();
    }

    return await serverGet(url);
};

/**
 * Drops warm entries without consuming them, for when a selection changes and the URLs warmed from
 * the previous choice can no longer be reached. There is no AbortController anywhere in this app,
 * so a request already in flight still completes -- this only stops the superseded entry sitting in
 * the map unconsumed until clearWarm().
 */
export const dropWarm = (urls) => {
    (Array.isArray(urls) ? urls : [urls]).filter(Boolean).forEach((url) => warmed.delete(url));
};

export const clearWarm = () => {
    warmed.clear();
};

/** Patient-scoped entries only; dropped when a booking is abandoned or restarted. */
export const clearFlowCache = () => {
    values.flow.clear();
    warmed.clear();
};

/** Full teardown, for logout and auth clean-up. */
export const clearRequestCache = () => {
    values.config.clear();
    values.flow.clear();
    warmed.clear();
    inFlight.clear();
    hydrated = false;
    clearHandoff();

    try {
        sessionStorage.removeItem(STORE_KEY);
    } catch (e) {
        console.error(e);
    }
};
